import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Wraps the page content. On every route change it briefly fades + lifts the
 * content out, then fades + lifts the new page in. Uses requestAnimationFrame
 * to avoid blocking the route paint.
 *
 * No external animation library required.
 */
export default function PageTransition({ children }) {
  const { pathname } = useLocation();
  const [stage, setStage] = useState('enter'); // 'enter' | 'idle'
  const [contentKey, setContentKey] = useState(pathname);

  useEffect(() => {
    if (pathname === contentKey) return undefined;
    // Phase 1: fade out current content
    setStage('exit');
    const t1 = setTimeout(() => {
      // Phase 2: swap children and fade in
      setContentKey(pathname);
      requestAnimationFrame(() => setStage('enter'));
      // Phase 3: settle
      const t2 = setTimeout(() => setStage('idle'), 60);
      return () => clearTimeout(t2);
    }, 180);
    return () => clearTimeout(t1);
  }, [pathname, contentKey]);

  // CRITICAL: at idle we must remove the transform/will-change entirely.
  // Any non-`none` transform on an ancestor creates a "containing block" that
  // makes descendant `position: fixed` elements (modals, drawers) position
  // relative to this wrapper instead of the viewport — breaking centering
  // and letting modals appear below the page fold.
  // https://developer.mozilla.org/en-US/docs/Web/CSS/transform#fixed_descendants
  const styles =
    stage === 'exit'
      ? { opacity: 0, transform: 'translateY(8px)', willChange: 'opacity, transform' }
      : stage === 'enter'
      ? { opacity: 0, transform: 'translateY(8px)', willChange: 'opacity, transform' }
      : { opacity: 1, transform: 'none', willChange: 'auto' };

  // Trigger the "enter" animation on the next frame after content swap.
  useEffect(() => {
    if (stage === 'enter') {
      const id = requestAnimationFrame(() => setStage('idle'));
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [stage]);

  return (
    <div
      style={{
        ...styles,
        transition: 'opacity 0.32s cubic-bezier(0.22,1,0.36,1), transform 0.32s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      {children}
    </div>
  );
}
